// Inventory Zustand Store

import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {
  InventoryItem,
  Location,
  TrackedSerial,
  InventoryMovement,
  InventoryFilters,
  InventoryMovementFilters,
  InventoryStatus
} from '../models/inventory.model';

interface InventoryState {
  // Data State
  inventoryItems: InventoryItem[];
  locations: Location[];
  trackedSerials: TrackedSerial[];
  inventoryMovements: InventoryMovement[];

  // UI State
  isLoading: boolean;
  error: string | null;
  filters: InventoryFilters;
  movementFilters: InventoryMovementFilters;
  selectedItem: InventoryItem | null;

  // Pagination
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;

  // Actions
  setInventoryItems: (items: InventoryItem[]) => void;
  setLocations: (locations: Location[]) => void;
  setTrackedSerials: (serials: TrackedSerial[]) => void;
  setInventoryMovements: (movements: InventoryMovement[]) => void;

  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setFilters: (filters: Partial<InventoryFilters>) => void;
  setMovementFilters: (filters: Partial<InventoryMovementFilters>) => void;
  setSelectedItem: (item: InventoryItem | null) => void;

  setPagination: (page: number, totalPages: number) => void;

  // Computed Properties
  lowStockItems: () => InventoryItem[];
  outOfStockItems: () => InventoryItem[];
  filteredItems: () => InventoryItem[];

  // State Reset
  resetFilters: () => void;
  resetStore: () => void;
}

const initialFilters: InventoryFilters = {
  locationId: undefined,
  productId: undefined,
  status: undefined,
  minQuantity: undefined,
  maxQuantity: undefined,
  lowStockOnly: false,
  outOfStockOnly: false
};

const initialMovementFilters: InventoryMovementFilters = {
  type: undefined,
  productId: undefined,
  locationId: undefined,
  performedBy: undefined,
  dateFrom: undefined,
  dateTo: undefined
};

export const useInventoryStore = create<InventoryState>()(
  devtools(
    (set, get) => ({
      // Initial State
      inventoryItems: [],
      locations: [],
      trackedSerials: [],
      inventoryMovements: [],

      isLoading: false,
      error: null,
      filters: initialFilters,
      movementFilters: initialMovementFilters,
      selectedItem: null,

      currentPage: 1,
      totalPages: 1,
      itemsPerPage: 20,

      // Actions
      setInventoryItems: (items) =>
        set({inventoryItems: items}, false, 'setInventoryItems'),

      setLocations: (locations) =>
        set({locations}, false, 'setLocations'),

      setTrackedSerials: (serials) =>
        set({trackedSerials: serials}, false, 'setTrackedSerials'),

      setInventoryMovements: (movements) =>
        set({inventoryMovements: movements}, false, 'setInventoryMovements'),

      setLoading: (loading) =>
        set({isLoading: loading}, false, 'setLoading'),

      setError: (error) =>
        set({error}, false, 'setError'),

      setFilters: (newFilters) =>
        set(
          (state) => ({filters: {...state.filters, ...newFilters}}),
          false,
          'setFilters'
        ),

      setMovementFilters: (newFilters) =>
        set(
          (state) => ({movementFilters: {...state.movementFilters, ...newFilters}}),
          false,
          'setMovementFilters'
        ),

      setSelectedItem: (item) =>
        set({selectedItem: item}, false, 'setSelectedItem'),

      setPagination: (page, totalPages) =>
        set({currentPage: page, totalPages}, false, 'setPagination'),

      // Computed Properties
      lowStockItems: () => {
        const {inventoryItems} = get();
        return inventoryItems.filter(item =>
          item.quantity <= item.minStockLevel && item.quantity > 0
        );
      },

      outOfStockItems: () => {
        const {inventoryItems} = get();
        return inventoryItems.filter(item =>
          item.quantity === 0 || item.status === InventoryStatus.OUT_OF_STOCK
        );
      },

      filteredItems: () => {
        const {inventoryItems, filters} = get();

        return inventoryItems.filter(item => {
          // Location filter
          if (filters.locationId && item.locationId !== filters.locationId) {
            return false;
          }

          // Product filter
          if (filters.productId && item.productId !== filters.productId) {
            return false;
          }

          // Status filter
          if (filters.status && item.status !== filters.status) {
            return false;
          }

          // Quantity range filters
          if (filters.minQuantity !== undefined && item.quantity < filters.minQuantity) {
            return false;
          }

          if (filters.maxQuantity !== undefined && item.quantity > filters.maxQuantity) {
            return false;
          }

          // Low stock filter
          if (filters.lowStockOnly && item.quantity > item.minStockLevel) {
            return false;
          }

          // Out of stock filter
          if (filters.outOfStockOnly && item.quantity > 0) {
            return false;
          }

          return true;
        });
      },

      // Reset Functions
      resetFilters: () =>
        set({filters: initialFilters, currentPage: 1}, false, 'resetFilters'),

      resetStore: () =>
        set({
          inventoryItems: [],
          locations: [],
          trackedSerials: [],
          inventoryMovements: [],
          isLoading: false,
          error: null,
          filters: initialFilters,
          movementFilters: initialMovementFilters,
          selectedItem: null,
          currentPage: 1,
          totalPages: 1,
          itemsPerPage: 20
        }, false, 'resetStore')
    }),
    {
      name: 'inventory-store'
    }
  )
);
